// src/pages/ELearning/ELearningLayout.tsx
import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate, useLocation, Outlet } from 'react-router-dom';
import {
  BookOpen, Award, PieChart, Settings, Gift, ClipboardList, Trophy, BarChart3, ShieldCheck, Target, Calendar as CalendarIcon, MessageSquare, LineChart, Route, Zap,
  ChevronLeft, ChevronRight, Home, Edit3, BarChart2, User
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Tooltip } from 'react-tooltip';
import ChatWidget from '../../components/ChatWidget';
import NotificationBell from '../../components/NotificationBell';
import useSidebarOrder from '../../hooks/useSidebarOrder';
import { SortableItem, EditOrderButton } from '../../components/SortableSidebar';
import api from '../../api/client';

const ELearningLayout = () => {
    const location = useLocation();
  const contentRef = React.useRef<HTMLDivElement>(null);

  // ═══ БЛОКИРОВКА СКРОЛЛА body/html ═══
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prev = {
      htmlOverflow: html.style.overflow,
      bodyOverflow: body.style.overflow,
      htmlHeight: html.style.height,
      bodyHeight: body.style.height,
    };
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    html.style.height = '100vh';
    body.style.height = '100vh';
    return () => {
      html.style.overflow = prev.htmlOverflow;
      body.style.overflow = prev.bodyOverflow;
      html.style.height = prev.htmlHeight;
      body.style.height = prev.bodyHeight;
    };
  }, []);

  // ─── Скролл контента вверх при смене роута ───
  useEffect(() => {
    // Сброс скролла ВНУТРЕННЕГО контейнера
    if (contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
    // И на всякий случай — window (не должен скроллиться, но пусть)
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [location.pathname]);
  const { user } = useAuth();

  // BASE_MENU_MOVED: объявлено здесь, чтобы использовать в useSidebarOrder ниже
  // MENU_ORDERED_V2: новый порядок + Автоправила скрыто
  const baseMenuItems = [
    { path: '/app/elearning', icon: <Home size={20} />, label: 'Главная' },
    { path: '/app/elearning/tests', icon: <Edit3 size={20} />, label: 'Тесты' },
    { path: '/app/elearning/calendar', icon: <CalendarIcon size={20} />, label: 'Календарь' },
    { path: '/app/elearning/constructor', icon: <Settings size={20} />, label: 'Конструктор' },
    { path: '/app/elearning/task-benefits', icon: <PieChart size={20} />, label: 'Задачи' },
    { path: '/app/elearning/benefits', icon: <Gift size={20} />, label: 'Бенефиты' },
    { path: '/app/elearning/achievements', icon: <Trophy size={20} />, label: 'Достижения' },
    { path: '/app/elearning/analytics', icon: <BarChart2 size={20} />, label: 'Аналитика' },
    { path: '/app/elearning/hr-dashboard', icon: <BarChart3 size={20} />, label: 'HR Дашборд' },
    { path: '/app/elearning/profile', icon: <User size={20} />, label: 'Мой профиль' },
    { path: '/app/elearning/rating', icon: <Award size={20} />, label: 'Рейтинг' },
    { path: '/app/elearning/comparative', icon: <LineChart size={20} />, label: 'Сравн. аналитика' },
    { path: '/app/elearning/my-assignments', icon: <ClipboardList size={20} />, label: 'Мои назначения' },
    { path: '/app/elearning/audit', icon: <ShieldCheck size={20} />, label: 'Журнал аудита' },
    { path: '/app/elearning/kpi', icon: <Target size={20} />, label: 'KPI' },
    { path: '/app/elearning/comments', icon: <MessageSquare size={20} />, label: 'Комментарии' },
    // Автоправила временно скрыто (работает на backend, UI появится позже)
    // Автоправила временно скрыто (backend работает, UI в разработке)
    { path: '/app/elearning/paths', icon: <Route size={20} />, label: 'Программы' },
  ];

  // ─── ФИЧА: DnD порядок меню (как в CRM) ───
  const { items: menuItems, reorder, reset, saving: savingOrder } = useSidebarOrder('elearning_v2', baseMenuItems);

  // ★ Фильтр по allowedSections
  const userAllowed: string[] = (user as any)?.permissions?.allowedSections || [];
  const userRole = (user as any)?.role;
  const isSuperAdmin = userRole === 'superadmin' || userRole === 'owner';
  const visibleItems = (isSuperAdmin || userAllowed.length === 0)
    ? menuItems
    : menuItems.filter((it: any) => userAllowed.includes(it.path));
  const [editOrder, setEditOrder] = React.useState(false);
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('elearning-sidebar-collapsed');
    return saved !== null ? JSON.parse(saved) : false;
  });
  const [bestEmployee, setBestEmployee] = useState({ name: 'Загрузка...', streak: 0 });

  useEffect(() => {
    const fetchBest = async () => {
      try {
        const res = await api.get('/elearning/rating/top-company');
        if (res.data) {
          setBestEmployee({ name: res.data.name, streak: res.data.elearning?.streak || 0 });
        }
      } catch (err) {
        console.warn('Не удалось загрузить лучшего сотрудника:', err);
      }
    };
    fetchBest();
  }, []);

  const toggleCollapsed = () => {
    const newState = !collapsed;
    setCollapsed(newState);
    localStorage.setItem('elearning-sidebar-collapsed', JSON.stringify(newState));
  };


  const sidebarWidth = collapsed ? 80 : 260;

  if (!user) {
    return null;
  }

  return (
    <div style={styles.container}>
      <div style={styles.backgroundImage} />
      <div style={{ ...styles.sidebar, width: sidebarWidth }}>
        <div style={styles.logo}>
          <img src="/images/10.png" alt="LAMA" style={styles.logoIcon} />
          {!collapsed && <span style={styles.logoText}>LAMA Универ</span>}
        </div>
        <nav style={styles.nav}>
          {/* ─── Заголовок с EditOrderButton ─── */}
          {!collapsed && (
            <div style={{
              padding: '8px 14px 0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <span style={{
                fontSize: 10,
                color: '#94a3b8',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
              }}>
                {editOrder ? '🖐️ Перетащите' : 'Меню'}
              </span>
              <EditOrderButton
                isEditMode={editOrder}
                onToggle={() => setEditOrder(!editOrder)}
                onReset={reset}
                saving={savingOrder}
              />
            </div>
          )}
          {visibleItems.map((item, idx) => (
            <SortableItem
                key={item.path}
                index={idx}
                total={visibleItems.length}
                onReorder={reorder}
                isEditMode={editOrder && !collapsed}
              >
              <NavLink
              to={item.path}
              draggable={false}
              onMouseDown={(e: any) => {
                // Предотвращаем фокус от мыши → нет scroll-into-view
                e.preventDefault();
              }}
              onClick={(e: any) => {
                // Убираем фокус → нет сброса scrollTop
                e.preventDefault();
                (e.currentTarget as HTMLElement).blur();
                navigate(item.path);
              }}
              style={({ isActive }) => ({
                ...styles.navItem,
                ...(isActive ? styles.navItemActive : {}),
                justifyContent: collapsed ? 'center' : 'flex-start',
              })}
              className="elearning-nav-item"
              data-tooltip-id={collapsed ? "elearning-tooltip" : undefined}
              data-tooltip-content={collapsed ? item.label : undefined}
              data-tooltip-place="right"
            >
              {item.icon}
              {!collapsed && <span style={styles.navLabel}>{item.label}</span>}
            </NavLink>
              </SortableItem>
          ))}
        </nav>
        <div style={styles.footer}>
          <button
            onClick={() => navigate('/dashboard')}
            className="elearning-nav-item"
            style={{
              ...styles.backButton,
              width: collapsed ? '40px' : '100%',
              justifyContent: collapsed ? 'center' : 'flex-start',
            }}
            data-tooltip-id={collapsed ? "elearning-tooltip" : undefined}
            data-tooltip-content={collapsed ? "На главный дашборд" : undefined}
            data-tooltip-place="top"
          >
            <ChevronLeft size={16} />
            {!collapsed && <span style={{ marginLeft: '8px' }}>На главную</span>}
          </button>
        </div>
      </div>

      <button
        onClick={toggleCollapsed}
        style={{ ...styles.toggleButton, left: sidebarWidth + 10 - 12 }}
      >
        {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
      </button>

      <div
        ref={contentRef}
        className="elearning-content-scroll"
        style={{ ...styles.content, marginLeft: sidebarWidth + 20 }}
      >
        {/* Адаптивная верхняя панель */}
        <div style={styles.topBar}>
          <div style={styles.ratingWidget}>
            <Award size={20} color="#f1c40f" />
            <span style={styles.ratingValue}>{user?.elearning?.rating ?? 0}</span>
            <span style={styles.ratingLabel}>Рейтинг</span>
          </div>
          <div style={styles.pointsWidget}>
            💰 Баллы: {user?.elearning?.totalPoints ?? 0}
          </div>
          <div style={styles.streakWidget}>
            🔥 Серия: {user?.elearning?.streak ?? 0} дн.
          </div>
          <div style={styles.bestEmployeeWidget}>
            🏆 {bestEmployee.name} ({bestEmployee.streak} раза)
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}>
            <NotificationBell module="hr" types="elearning" accentColor="#667eea" />
          </div>
        </div>
        <div className="elearning-page-content" style={styles.pageContent} key={location.pathname}>
          <Outlet />
        </div>
      </div>

      <ChatWidget />

      <Tooltip id="elearning-tooltip" delayShow={0} delayHide={100} style={{ fontSize: 12, backgroundColor: 'rgba(0,0,0,0.8)', borderRadius: 8, padding: '4px 8px', zIndex: 10000 }} />
      <style>{`
        .elearning-nav-item:hover {
          background: rgba(37, 117, 252, 0.15) !important;
          border-radius: 30px !important;
        }
        .elearning-nav-item:active {
          background: rgba(37, 117, 252, 0.25) !important;
        }
        /* Плавное появление страниц */
        .elearning-page-content {
          animation: elearningFadeIn 0.25s ease-out;
        }
        @keyframes elearningFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        /* Убираем прыжок скроллбара */
        .elearning-content-scroll {
          transition: margin-left 0.3s ease;
        }
      `}</style>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: { height: '100vh', overflow: 'hidden', position: 'relative' },
  backgroundImage: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: -1,
    backgroundImage: 'url("/images/crm-background2.jpg")',
    backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.25,
  },
  sidebar: {
    position: 'fixed', left: 10, top: 10, bottom: 10,
    background: 'rgba(255,255,255,0.25)', backdropFilter: 'blur(12px)',
    borderRadius: 24, border: '1px solid rgba(255,255,255,0.3)',
    boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
    display: 'flex', flexDirection: 'column',
    transition: 'width 0.3s', zIndex: 1000,
    overscrollBehavior: 'contain',
  },
  logo: { display: 'flex', alignItems: 'center', gap: 8, padding: '20px 16px', borderBottom: '1px solid rgba(255,255,255,0.2)' },
  logoIcon: { height: 32, width: 'auto' },
  logoText: { fontSize: 18, fontWeight: 700, background: 'linear-gradient(135deg, #6a11cb, #2575fc)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' },
  nav: { 
    flex: 1, 
    padding: '20px 0', 
    overflowY: 'auto',
    overscrollBehavior: 'contain',
    scrollBehavior: 'auto',
  },
  navItem: {
    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
    margin: '4px 8px', borderRadius: 30, color: '#1a1a2e',
    textDecoration: 'none', fontSize: 14, fontWeight: 500,
    transition: 'background 0.2s',
  },
  navItemActive: { background: 'rgba(37,117,252,0.25)', color: '#2575fc' },
  navLabel: { whiteSpace: 'nowrap' },
  footer: { padding: '20px 16px', borderTop: '1px solid rgba(255,255,255,0.2)' },
  backButton: {
    background: 'rgba(255,255,255,0.3)', backdropFilter: 'blur(4px)',
    border: '1px solid rgba(255,255,255,0.3)', borderRadius: 30,
    padding: '10px 16px', color: '#1a1a2e', cursor: 'pointer',
    display: 'flex', alignItems: 'center', fontSize: 14, fontWeight: 500,
    transition: 'all 0.2s',
  },
  toggleButton: {
    position: 'fixed', top: 60, width: 24, height: 24, borderRadius: 12,
    background: 'white', border: '1px solid #2575fc', color: '#2575fc',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1001, transition: 'left 0.3s',
  },
  content: { transition: 'margin-left 0.3s', height: '100vh', overflowY: 'auto', overflowX: 'hidden', paddingTop: 20, paddingBottom: 40, paddingRight: 20, WebkitOverflowScrolling: 'touch' },
  topBar: {
    margin: '0 20px 20px 20px', padding: '12px 24px',
    background: 'rgba(255,255,255,0.5)', backdropFilter: 'blur(8px)',
    borderRadius: 40, border: '1px solid rgba(255,255,255,0.3)',
    boxShadow: '0 8px 20px rgba(0,0,0,0.05)',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
    flexWrap: 'wrap',       // ← теперь перенос элементов
  },
  ratingWidget: { display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 },
  ratingValue: { fontSize: 20, fontWeight: 700, color: '#1a1a2e' },
  ratingLabel: { fontSize: 14, color: '#666' },
  pointsWidget: { fontSize: 14, fontWeight: 500, color: '#2ecc71', background: 'rgba(46,204,113,0.1)', padding: '4px 12px', borderRadius: 20, flexShrink: 0 },
  streakWidget: { fontSize: 14, fontWeight: 500, color: '#e67e22', background: 'rgba(230,126,34,0.1)', padding: '4px 12px', borderRadius: 20, flexShrink: 0 },
  bestEmployeeWidget: { fontSize: 14, fontWeight: 500, color: '#8e44ad', background: 'rgba(142,68,173,0.1)', padding: '4px 12px', borderRadius: 20, flexShrink: 0 },
  pageContent: { padding: '0 20px 20px 20px' },
};

export default ELearningLayout;